/** Everything about the open table (06 §1.4). */
import { computed, signal } from '@preact/signals';
import type { Action, CardId } from '@tarock/engine/cards';
import type { MsgKey } from '@tarock/i18n';
import type { ChatMsg, Presence, Seat, TableView, You } from '@tarock/protocol';
import type { ConnStatus, TableSource } from '../table/source';

export type { ConnStatus } from '../table/source';

/** The unacked game action (at most one, 06 §7.4). */
export interface PendingAct {
  cid: string;
  action: Action;
  /** The `seq` the action was chosen against (a `rate` retry keeps it, so it can still go `stale`). */
  expSeq?: number;
  /** Retried once after a `rate` ack (06 §1.5 step 4). */
  retried?: boolean;
}

/** A `peekResult` (04 §10.2). */
export type PeekResult =
  | { what: 'ownTricks'; tricks: CardId[][] }
  | { what: 'lastTrick'; trick: { leader: Seat; cards: CardId[]; winner: Seat } | null };

/** The table signals. `server` follows the source; `display` is advanced by the Director. */
export const tableStore = {
  source: signal<TableSource | null>(null),
  conn: signal<ConnStatus>('idle'),
  you: signal<You | null>(null),
  server: signal<{ seq: number; gseq: number; view: TableView } | null>(null),
  display: signal<{ seq: number; view: TableView } | null>(null),
  pending: signal<PendingAct | null>(null),
  presence: signal<Partial<Record<Seat, Presence>>>({}),
  paused: signal<{ seat: Seat | null; reason: string; since: number } | null>(null),
  chat: signal<ChatMsg[]>([]),
  peek: signal<PeekResult | null>(null),
  /** Set on the ack of my redeal answer; my own answer event is held until the step resolves (06 §8.2). */
  answeredRedeal: signal(false),
  /** A transient message ("That didn't go through…"), as an i18n key. */
  /** A transient message: `text` (already filled in) wins over `key`. */
  toast: signal<{ key: MsgKey; at: number; text?: string; ms?: number } | null>(null),
};

/** My legal actions, own seat only. */
export const legal = computed(() => tableStore.server.value?.view.game?.legal ?? null);

/** Whether I may act now (06 §1.4). */
export const inputEnabled = computed(() => {
  const s = tableStore.server.value;
  const d = tableStore.display.value;
  const you = tableStore.you.value;
  if (!s || !d || !you || you.seat === null || d.seq !== s.seq) {
    return false;
  }
  const g = s.view.game;
  // `legal` is the viewer's own: a view of another perspective never enables input (play-test M2)
  if (!g || g.viewer !== you.seat) {
    return false;
  }
  // the turn must be mine both where the server is and on screen
  const shown = g.phase === 'redealCheck' || d.view.game?.toAct === you.seat;
  const myTurn = (g.toAct === you.seat && shown) || (g.phase === 'redealCheck' && !tableStore.answeredRedeal.value);
  return (
    myTurn &&
    (legal.value?.length ?? 0) > 0 &&
    tableStore.conn.value === 'open' &&
    tableStore.paused.value === null &&
    tableStore.pending.value === null
  );
});

/** Clears the table state when a table is left. */
export function resetTableStore(): void {
  tableStore.source.value = null;
  tableStore.conn.value = 'idle';
  tableStore.you.value = null;
  tableStore.server.value = null;
  tableStore.display.value = null;
  tableStore.pending.value = null;
  tableStore.presence.value = {};
  tableStore.paused.value = null;
  tableStore.chat.value = [];
  tableStore.peek.value = null;
  tableStore.answeredRedeal.value = false;
  tableStore.toast.value = null;
}
